import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FileText, Layers, Package } from 'lucide-react';
import { resolveImageUrl } from '../../lib/imageCache';
import { CONFIG_PRODUCTS, configureUrl } from '../website/sections/fabricProducts';

const Group = ({ title, icon: Icon, children, testid }) => (
  <section className="py-2" data-testid={testid}>
    <h3 className="px-3 pb-1.5 text-[11px] uppercase tracking-[0.18em] text-neutral-500 flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" />{title}</h3>
    <ul>{children}</ul>
  </section>
);

const rowCls = (active) => `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${active ? 'bg-amber-50' : 'hover:bg-neutral-50'}`;

// Grouped results; `items` is the flat keyboard order (rendered index = position in items).
export const SearchResults = ({ r, active, onPick, compact }) => {
  let i = -1;
  const next = () => { i += 1; return i; };
  const page = (d) => { const n = next(); return (
    <li key={d.path} data-search-idx={n}>
      <Link to={d.path} onClick={onPick} className={rowCls(active === n)} data-testid={`search-result-${d.path.replace(/[^a-z0-9]+/gi, '-')}`}>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-neutral-900 truncate">{d.title}</p>
          {d.description && <p className="text-xs text-neutral-500 line-clamp-1">{d.description}</p>}
        </div>
        <ArrowRight className="w-4 h-4 text-neutral-300 shrink-0" />
      </Link>
    </li>
  ); };
  return (
    <div className="divide-y divide-neutral-100">
      {r.products.length > 0 && <Group title="Products" icon={Package} testid="search-group-products">{r.products.map(page)}</Group>}
      {r.fabrics.length > 0 && (
        <Group title={`Fabrics (${r.fabric_total})`} icon={Layers} testid="search-group-fabrics">
          {r.fabrics.slice(0, compact ? 6 : 12).map((f) => { const n = next(); return (
            <li key={f.id} data-search-idx={n} className={rowCls(active === n)} data-testid={`search-fabric-${f.id}`}>
              <img src={resolveImageUrl(f.image_url, 128)} alt="" className="w-11 h-11 rounded-md object-cover bg-neutral-100 shrink-0" loading="lazy" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-neutral-900 truncate">{f.name}{f.code && !f.name.startsWith(f.code) ? <span className="hidden sm:inline text-neutral-400 font-normal"> · {f.code}</span> : null}</p>
                {f.code && !f.name.startsWith(f.code) && <p className="sm:hidden text-[11px] text-neutral-400 truncate">{f.code}</p>}
                <p className="text-xs text-neutral-500 truncate">{[f.material, f.collection, f.brand].filter(Boolean).join(' · ')}</p>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <Link to={`/products?fabric=${f.id}`} onClick={onPick} className="text-xs px-2.5 py-1.5 rounded-full border border-neutral-200 hover:border-neutral-900" data-testid={`search-fabric-view-${f.id}`}>View</Link>
                <Link to={configureUrl(f.primary_product, f)} onClick={onPick} className="text-xs px-2.5 py-1.5 rounded-full bg-neutral-900 text-white hover:bg-neutral-700" title={`Configure as ${CONFIG_PRODUCTS[f.primary_product]}`} data-testid={`search-fabric-configure-${f.id}`}>Configure</Link>
              </div>
            </li>
          ); })}
          {r.fabric_total > r.fabrics.length && (
            <li className="px-3 pt-1"><Link to={`/products?fabric_q=${encodeURIComponent(r.query)}#fabric-finder`} onClick={onPick} className="text-xs font-medium text-amber-700 hover:underline" data-testid="search-fabrics-all">See all {r.fabric_total} fabrics in the Fabric Finder</Link></li>
          )}
        </Group>
      )}
      {r.collections.length > 0 && (
        <Group title="Collections" icon={Layers} testid="search-group-collections">
          {r.collections.map((c) => { const n = next(); const label = c.material || c.collection; return (
            <li key={`${c.brand}-${c.collection}-${c.material}`} data-search-idx={n}>
              <Link to={`/products?${c.material ? `fabric_mat=${encodeURIComponent(c.material)}&fabric_coll=${encodeURIComponent(c.collection)}` : `fabric_coll=${encodeURIComponent(c.collection)}`}#fabric-finder`} onClick={onPick} className={rowCls(active === n)} data-testid={`search-collection-${[c.brand, c.collection, c.material].filter(Boolean).join('-').replace(/[^a-z0-9]+/gi, '-')}`}>
                <img src={resolveImageUrl(c.image_url, 128)} alt="" className="w-11 h-11 rounded-md object-cover bg-neutral-100 shrink-0" loading="lazy" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-neutral-900 truncate">{[c.brand, label].filter(Boolean).join(' ')}</p>
                  <p className="text-xs text-neutral-500 truncate">{[c.material ? `${c.collection} collection` : null, `${c.count} ${c.count === 1 ? 'fabric' : 'fabrics'}`].filter(Boolean).join(' · ')}</p>
                </div>
              </Link>
            </li>
          ); })}
        </Group>
      )}
      {r.pages.length > 0 && <Group title="Guides & Pages" icon={FileText} testid="search-group-pages">{r.pages.map(page)}</Group>}
    </div>
  );
};

export const countItems = (r, compact) => (r ? r.products.length + Math.min(r.fabrics.length, compact ? 6 : 12) + r.collections.length + r.pages.length : 0);
